import { Injectable } from "@angular/core";
import { ApiBaseUrls, ApiControllers } from "../constants/constants";
import { CommonChecks } from "../utilities/common-checks.utilities";
import { CreateMemberRequest, MemberModel, UpdateMemberRequest, UpdateMemberSettingsRequest } from "../models/data/member.model";
import { MemberDirectoryEntryModel } from "../models/data/member-directory-entry.model";
import { MemberDirectoryFilter } from "../filters/member-directory.filter";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs/internal/firstValueFrom";
import { MembersFilter } from "../filters/members.filter";
import { PaginatedListModel } from "../models/service/paginated-list.model";
import { PaginationResponseHeader } from "../models/service/pagination-response-header.model";
import { BulkActionRequest } from "../models/service/bulk-action-request.model";
import { BulkActionResponse } from "../models/service/bulk-action-response.model";
import { ServiceResultModel } from "../models/service/service-result.model";
import { APISanitizerUtilities } from "../utilities/api-sanitizer.utilities";
import { ApiResponseUtilities } from "../utilities/api-response.utilities";
import { ServiceUtilities } from "../utilities/service.utilities";
import { AuditLog } from "../models/service/audit-log.model";
import { MemberAuditLogModel } from "../models/data/member-audit-log.model";
import { AuditLogMapper } from "../utilities/audit-log-mapper.utilities";
import { MemberStatusActions } from "../enums/data.enums";
import { AcceptMemberInvitationRequest } from "../models/service/accept-member-invitation.request";
import { PasswordResetModel } from "../models/data/password-reset.model";
import { MembersMessagingConstants } from "../constants/messaging.constants";

@Injectable({
  providedIn: 'root'
})
export class MemberService {
  private apiPath: string = `${ApiBaseUrls.CORE()}${ApiControllers.MEMBERS()}`;

  constructor(private httpClient: HttpClient) { }

  public async searchMembers(membersFilter: MembersFilter, orderBy?: string, skip?: number, take?: number): Promise<ServiceResultModel<PaginatedListModel<MemberModel>>> {
    try {
      orderBy = (orderBy) && encodeURIComponent(orderBy);
      const requestUrl = this.apiPath + `/filter?orderBy=${orderBy}&skip=${skip}&take=${take}`;

      const response = await firstValueFrom(
        this.httpClient.post<MemberModel[]>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<MembersFilter>(membersFilter)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
      const paginatedList: PaginatedListModel<MemberModel> = {
        data: <MemberModel[]>response.body?.valueOf(),
        total: paginationHeader?.total,
        skip: paginationHeader?.skip,
        take: paginationHeader?.take,
        hasPrevious: paginationHeader?.hasPrevious,
        hasNext: paginationHeader?.hasNext
      };

      return <ServiceResultModel>{ data: paginatedList, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  /**
   * The member directory: contact details for the members of one organization who have not opted out.
   *
   * Separate from searchMembers because it is a different contract with a different audience — it
   * returns MemberDirectoryEntryModel (contact details only, no status/role/user link) and is reachable
   * by a plain Member, who holds no members.* permission and cannot call /members/filter at all.
   */
  public async searchMemberDirectory(directoryFilter: MemberDirectoryFilter, skip: number, take: number): Promise<ServiceResultModel<PaginatedListModel<MemberDirectoryEntryModel>>> {
    try {
      const requestUrl = this.apiPath + `/directory?skip=${skip}&take=${take}`;

      const response = await firstValueFrom(
        this.httpClient.post<MemberDirectoryEntryModel[]>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<MemberDirectoryFilter>(directoryFilter)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const data = <MemberDirectoryEntryModel[]>response.body?.valueOf() ?? [];
      const paginationHeader: PaginationResponseHeader | undefined = ServiceUtilities.parsePaginationHeader(response);

      const paginatedList: PaginatedListModel<MemberDirectoryEntryModel> = {
        data: data,
        total: paginationHeader?.total ?? data.length,
        skip: paginationHeader?.skip ?? skip,
        take: paginationHeader?.take ?? take,
        hasPrevious: paginationHeader?.hasPrevious ?? skip > 0,
        // Without the header, keep going while pages come back non-empty, rather than treating a short
        // page as the end. A server free to return fewer rows than asked for — its own page cap, rows
        // filtered out after paging — would otherwise end the scroll on the very first page. The cost
        // is one empty request at the end; the caller stops when nothing new arrives.
        hasNext: paginationHeader?.hasNext ?? data.length > 0
      };

      return <ServiceResultModel>{ data: paginatedList, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  // Only the member themselves may call this; the API answers 403 for anyone else.
  public async updateMemberSettings(memberId: number, settings: UpdateMemberSettingsRequest): Promise<ServiceResultModel<MemberModel>> {
    try {
      const requestUrl = this.apiPath + `/${memberId}/settings`;

      const response = await firstValueFrom(
        this.httpClient.patch<MemberModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<UpdateMemberSettingsRequest>(settings)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const updatedMember = <MemberModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: updatedMember, success: true, message: MembersMessagingConstants.MEMBERS_DIRECTORY_VISIBILITY_SAVE_SUCCESS(), statusCode: response.status };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error, MembersMessagingConstants.MEMBERS_DIRECTORY_VISIBILITY_SAVE_FAILURE());
    }
  }

  public async getMemberById(memberId: number): Promise<ServiceResultModel<MemberModel>> {
    try {
      const requestUrl = this.apiPath + `/${memberId}`;
      const response = await firstValueFrom(this.httpClient.get<MemberModel>(requestUrl, { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }));

      const user = <MemberModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: user, success: true, statusCode: response.status }
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async createMember(member: CreateMemberRequest): Promise<ServiceResultModel<MemberModel | undefined>> {
    try {
      const requestUrl = this.apiPath;

      if (CommonChecks.isNullOrUndefinedOrEmptyOrWhitespace(member.phoneNumber)) {
        member.phoneCountryCode = undefined;
        member.phoneNumber = undefined;
        member.phoneExtension = undefined;
      }

      const response = await firstValueFrom(
        this.httpClient.post<MemberModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<CreateMemberRequest>(member)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const createdMember = <MemberModel>response.body?.valueOf();
      return { data: createdMember, success: true, message: MembersMessagingConstants.MEMBERS_CREATE_SUCCESS(), statusCode: response.status };
    } catch (error: any) {
      if (ApiResponseUtilities.isConflictStatusCode(error?.error?.statusCode)) {
        return <ServiceResultModel>{ success: false, message: MembersMessagingConstants.MEMBERS_ERROR_EMAIL_IN_USE(), statusCode: error?.error?.statusCode };
      }

      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async updateMember(memberId: number, member: UpdateMemberRequest): Promise<ServiceResultModel<MemberModel>> {
    try {
      const requestUrl = this.apiPath + `/${memberId}`;

      if (CommonChecks.isNullOrUndefinedOrEmptyOrWhitespace(member.phoneNumber)) {
        member.phoneCountryCode = undefined;
        member.phoneNumber = undefined;
        member.phoneExtension = undefined;
      }

      const response = await firstValueFrom(
        this.httpClient.patch<MemberModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<UpdateMemberRequest>(member)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const updatedMember = <MemberModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: updatedMember, success: true, message: MembersMessagingConstants.MEMBERS_EDIT_SUCCESS(), statusCode: response.status };
    } catch (error: any) {
      if (ApiResponseUtilities.isConflictStatusCode(error?.error?.statusCode)) {
        return <ServiceResultModel>{ success: false, message: MembersMessagingConstants.MEMBERS_ERROR_EMAIL_IN_USE(), statusCode: error?.error?.statusCode };
      }

      return ServiceUtilities.handleServiceException(error, MembersMessagingConstants.MEMBERS_EDIT_ERROR());
    }
  }

  public async updateMemberStatus(action: MemberStatusActions, member: MemberModel, note?: string): Promise<ServiceResultModel<MemberModel>> {
    try {
      const requestUrl = `${this.apiPath}/${member.memberId}/status/${action}`;

      const response = await firstValueFrom(
        this.httpClient.patch<MemberModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<string | undefined>(note)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      let actionPastTense = "";
      switch (action) {
        case MemberStatusActions.Activate: 
          actionPastTense = "activated";
          break;
        case MemberStatusActions.Deactivate: 
          actionPastTense = "deactivated";
          break;
        case MemberStatusActions.Suspend: 
          actionPastTense = "suspended";
          break;
        case MemberStatusActions.Unsuspend: 
          actionPastTense = "unsuspended";
          break;
      }

      const successMessageData = {actionText: actionPastTense, firstName: member.firstName, lastName: member.lastName};
      const successMessage = MembersMessagingConstants.MEMBERS_UPDATE_MEMBER_STATUS_SUCCESS().replaceValuesWithObject(successMessageData);

      const updatedMember = <MemberModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: updatedMember, success: true, message: successMessage, statusCode: response.status }
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async deleteMember(memberId: number, note?: string): Promise<ServiceResultModel<MemberModel>> {
    try {
      const requestUrl = this.apiPath + `/${memberId}`;
      const response = await firstValueFrom(
        this.httpClient.delete<MemberModel>(
          requestUrl, 
          {
            body: JSON.stringify(APISanitizerUtilities.sanitizePayload<string | undefined>(note)),
            observe: "response", 
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } 
          }
        ));
      const member = <MemberModel>response.body?.valueOf();

      const successMessageData = {firstName: member.firstName, lastName: member.lastName};
      const successMessage = MembersMessagingConstants.MEMBERS_DELETE_MEMBER_SUCCESS().replaceValuesWithObject(successMessageData);

      return <ServiceResultModel>{ data: member, success: true, message: successMessage, statusCode: response.status }
    }
    catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getAuditLogsByMemberId(memberId: number, skip: number, take: number, includeDetails: boolean = false): Promise<ServiceResultModel<PaginatedListModel<AuditLog>>> {
    try {
      let requestUrl = this.apiPath + `/${memberId}/logs?skip=${skip}&take=${take}`;
      if (includeDetails) {
        requestUrl += `&includeDetails=true`;
      }
      const response = await firstValueFrom(this.httpClient.get<MemberAuditLogModel[]>(requestUrl, { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }));
      const mappedData = AuditLogMapper.mapMemberAuditLogs(<MemberAuditLogModel[]>response.body?.valueOf());

      const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
      const paginatedList: PaginatedListModel<AuditLog> = {
        data: mappedData,
        total: paginationHeader?.total,
        skip: paginationHeader?.skip,
        take: paginationHeader?.take,
        hasPrevious: paginationHeader?.hasPrevious,
        hasNext: paginationHeader?.hasNext
      };

      return { data: paginatedList, success: true, message: "Audit logs loaded successfully." };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async bulkUpdateMemberStatuses(bulkAction: MemberStatusActions, bulkActionRequest: BulkActionRequest<MembersFilter | null>): Promise<ServiceResultModel<BulkActionResponse>> {
    try {
      const requestUrl = `${this.apiPath}/bulk/status/${bulkAction}`;

      const response = await firstValueFrom(
        this.httpClient.patch<BulkActionResponse>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<BulkActionRequest<MembersFilter | null>>(bulkActionRequest)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      let actionPastTense = "";
      switch (bulkAction) {
        case MemberStatusActions.Activate: 
          actionPastTense = "activated";
          break;
        case MemberStatusActions.Deactivate: 
          actionPastTense = "deactivated";
          break;
        case MemberStatusActions.Suspend: 
          actionPastTense = "suspended";
          break;
        case MemberStatusActions.Unsuspend: 
          actionPastTense = "unsuspended";
          break;
      }
      
      const bulkActionResponse = <BulkActionResponse>response.body?.valueOf();

      const successMessageData = {actionText: actionPastTense, totalAffected: bulkActionResponse.totalAffected, totalSkipped: bulkActionResponse.totalSkipped};
      const successMessage = MembersMessagingConstants.MEMBERS_BULK_ACTION_STATUS_UPDATE_SUCCESS().replaceValuesWithObject(successMessageData);

      return <ServiceResultModel>{ data: bulkActionResponse, success: true, message: successMessage, statusCode: response.status }
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async bulkDeleteMembers(bulkActionRequest: BulkActionRequest<MembersFilter | null>): Promise<ServiceResultModel<BulkActionResponse>> {
    try {
      const requestUrl = `${this.apiPath}/bulk`;

      const response = await firstValueFrom(
        this.httpClient.delete<BulkActionResponse>(
          requestUrl,
          { 
            observe: "response", 
            body: JSON.stringify(APISanitizerUtilities.sanitizePayload<BulkActionRequest<MembersFilter | null>>(bulkActionRequest)),
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } 
          }
        ));

      const bulkActionResponse = <BulkActionResponse>response.body?.valueOf();

      const successMessageData = {totalAffected: bulkActionResponse.totalAffected, totalSkipped: bulkActionResponse.totalSkipped};
      const successMessage = MembersMessagingConstants.MEMBERS_BULK_DELETE_SUCCESS().replaceValuesWithObject(successMessageData);

      return <ServiceResultModel>{ data: bulkActionResponse, success: true, message: successMessage, statusCode: response.status }
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async bulkSendInvitations(bulkActionRequest: BulkActionRequest<MembersFilter | null>): Promise<ServiceResultModel<BulkActionResponse>> {
    try {
      const requestUrl = `${this.apiPath}/bulk/invite`;

      const response = await firstValueFrom(
        this.httpClient.patch<BulkActionResponse>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<BulkActionRequest<MembersFilter | null>>(bulkActionRequest)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const bulkActionResponse = <BulkActionResponse>response.body?.valueOf();

      const successMessageData = {totalAffected: bulkActionResponse.totalAffected, totalEvaluated: bulkActionResponse.totalEvaluated, totalSkipped: bulkActionResponse.totalSkipped};
      const successMessage = MembersMessagingConstants.MEMBERS_BULK_INVITE_SUCCESS().replaceValuesWithObject(successMessageData);

      return <ServiceResultModel>{ data: bulkActionResponse, success: true, message: successMessage, statusCode: response.status }
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async bulkCancelInvitations(bulkActionRequest: BulkActionRequest<MembersFilter | null>): Promise<ServiceResultModel<BulkActionResponse>> {
    try {
      const requestUrl = `${this.apiPath}/bulk/cancel-invite`;

      const response = await firstValueFrom(
        this.httpClient.patch<BulkActionResponse>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<BulkActionRequest<MembersFilter | null>>(bulkActionRequest)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const bulkActionResponse = <BulkActionResponse>response.body?.valueOf();

      const successMessageData = {totalAffected: bulkActionResponse.totalAffected, totalEvaluated: bulkActionResponse.totalEvaluated, totalSkipped: bulkActionResponse.totalSkipped};
      const successMessage = MembersMessagingConstants.MEMBERS_BULK_UNINVITE_SUCCESS().replaceValuesWithObject(successMessageData);

      return <ServiceResultModel>{ data: bulkActionResponse, success: true, message: successMessage, statusCode: response.status }
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async transferOrganizationOwnership(memberId: number): Promise<ServiceResultModel<void>> {
    try {
      const requestUrl = this.apiPath + `/${memberId}/transfer-ownership`;
      const response = await firstValueFrom(
        this.httpClient.patch<void>(
          requestUrl,
          null,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        ));

      return <ServiceResultModel>{ 
        success: true, 
        message: MembersMessagingConstants.MEMBERS_TRANSFER_ORG_OWNERSHIP(),
        statusCode: response.status 
      };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async grantRoleToMember(memberId: number, roleId: number): Promise<ServiceResultModel<void>> {
    try {
      const requestUrl = this.apiPath + `/${memberId}/roles`;
      const response = await firstValueFrom(
        this.httpClient.post<void>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<{ roleIds: number[] }>({ roleIds: [roleId] })),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      return <ServiceResultModel>{ 
        success: true, 
        message: MembersMessagingConstants.MEMBERS_ADD_ROLE_SUCCESS(),
        statusCode: response.status 
      };
    } catch (error: any) {
      console.error(error);
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async revokeRoleFromMember(memberId: number, roleId: number): Promise<ServiceResultModel<void>> {
    try {
      const requestUrl = this.apiPath + `/${memberId}/roles`;
      const response = await firstValueFrom(
        this.httpClient.delete<void>(
          requestUrl,
          {
            body: JSON.stringify(APISanitizerUtilities.sanitizePayload<{ roleIds: number[] }>({ roleIds: [roleId] })),
            observe: "response",
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
          }
        ));

      return <ServiceResultModel>{ 
        success: true, 
        message: MembersMessagingConstants.MEMBERS_ROLE_REMOVAL_SUCCESS(),
        statusCode: response.status 
      };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async sendMemberInvitationEmail(memberId: number): Promise<ServiceResultModel<void>> {
    try {
      const requestUrl = this.apiPath + `/${memberId}/invite`;
      const response = await firstValueFrom(
        this.httpClient.patch<void>(
          requestUrl,
          null,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        ));
      return <ServiceResultModel>{ 
        success: true, 
        message: "Member invitation email has been sent successfully.",
        statusCode: response.status 
      };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async cancelMemberInvitation(memberId: number): Promise<ServiceResultModel<void>> {
    try {
      const requestUrl = this.apiPath + `/${memberId}/cancel-invite`;
      const response = await firstValueFrom(
        this.httpClient.patch<void>(
          requestUrl,
          null,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        ));
      return <ServiceResultModel>{ 
        success: true, 
        message: "Member invitation has been cancelled successfully.",
        statusCode: response.status 
      };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async updateMemberLastLogin(memberId: number): Promise<ServiceResultModel<void>> {
    try {
      const requestUrl = this.apiPath + `/${memberId}/last-login`;
      const response = await firstValueFrom(
        this.httpClient.patch<void>(
          requestUrl,
          null,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        ));

      return <ServiceResultModel>{ success: true, statusCode: response.status };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async acceptMemberInvitation(token: string): Promise<ServiceResultModel<PasswordResetModel>> {
    try {
      const requestUrl = this.apiPath + '/accept-invite';

      const acceptInviteRequest = <AcceptMemberInvitationRequest>{ token: token };
      const response = await firstValueFrom(
        this.httpClient.post(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<AcceptMemberInvitationRequest>(acceptInviteRequest)),
          { 
            observe: "response", 
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } 
          }
        ));

      const passwordReset = <PasswordResetModel>response.body?.valueOf();
      return <ServiceResultModel>{
        success: true,
        message: "Member invitation accepted successfully.",
        data: (passwordReset) ? passwordReset : undefined,
        statusCode: response.status
      };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }
}
